// Route guard for the gerencial tablero (ADR-0043 access matrix, amended
// ADR-0089).
//
// UX-only gate: server authorization is enforced independently on every
// /facility-dashboard (and related) endpoint in core-api via the
// DASHBOARD_SHELL_ROLE_KEYS check + per-request asset scoping. This guard
// exists so an excluded role never sees a dashboard shell it has no data
// for.
//
// Matrix:
//   admin, company_manager ("Gerente")  → full org dashboard
//   store_manager ("Jefe de tienda")    → dashboard scoped to their store
//   brand_manager                       → dashboard scoped to their brand
//   facility_supervisor ("Supervisor")  → dashboard scoped to their
//                                          assigned store(s) or zona
//   inspector (residential)             → never reaches a facility surface
//
// Compose inside RequireAuth:
//   <RequireAuth><RequireDashboardRole><Shell /></RequireDashboardRole></RequireAuth>

import { Navigate, useLocation } from "react-router-dom";
import { lazy, Suspense, type ReactNode } from "react";
import { isDashboardShellRole } from "@bitacorapro/api-types";
import { useSession } from "./useSession";

// Lazy: the terminal screen pulls in the QR encoder (`uqr`), which the
// 99% path (a shell role) never needs — keep it out of the main chunk.
const NoDashboardAccess = lazy(() =>
  import("./NoDashboardAccess").then((m) => ({ default: m.NoDashboardAccess }))
);

export function RequireDashboardRole({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (!session) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  if (!isDashboardShellRole(session.organization.role)) {
    // Terminal message rather than a redirect — bouncing to a gated route
    // would loop, and "/" is itself behind this guard.
    return (
      <Suspense fallback={null}>
        <NoDashboardAccess />
      </Suspense>
    );
  }
  return <>{children}</>;
}
